import * as PIXI from 'pixi.js';
import { Howl, Howler } from 'howler';

const _soundCache = new Map();

const loadSound = (url, options = {}) => {
    if (_soundCache.has(url)) {
        return Promise.resolve(_soundCache.get(url));
    }

    return new Promise((resolve, reject) => {
        const sound = new Howl({
            ...options,
            src: [url],
        });

        sound.on('load', () => {
            _soundCache.set(url, sound);
            resolve(sound);
        });

        sound.on('loaderror', (err) => {
            console.error('Failed to load sound:', err);
            reject(err);
        });
    });
}

export default class AssetsLoader {
    constructor() {
    }
    
    static eDefaultAssetsAliases = {
        MAIN_DECK_CARD_BACK: 'main_deck_card_back',
        DON_DECK_CARD_BACK: 'don_card_back',
        DON_CARD_FACE: 'don_card_face',
        PLAYER_BACKGROUND: 'player_background',
        OPPONENT_BACKGROUND: 'enemy_background',
        MAIN_DECK: 'main_deck',
        LEADER_FRAME: 'leader_frame',
        LIFE_ICON: 'life_icon',
        DON_DECK: 'don_deck',
        ACTIVE_DONS: 'active_dons',
        ACTIVE_DONS_FRAME: 'active_dons_frame',
        RESTED_DONS: 'rested_dons',
        STAGE_FRAME: 'stage_frame',
        TRASH_FRAME: 'trash_frame',
        CARD_FADE: 'card_fade',
        HIGHLIGHT_EFFECT_NOISE: 'highlight_effect_noise',
        CARD_INNER_FRAME: 'card_inner_frame',
        PHASE_BUTTON: 'phase_button',
        CANCEL_BUTTON: 'cancel_button',
        END_TURN_BUTTON: 'end_turn_button',
        ENEMY_BLOCKER_BUTTON: 'enemy_blocker_button',
        ENEMY_COUNTER_BUTTON: 'enemy_counter_button',
        ENEMY_TURN_BUTTON: 'enemy_turn_button',
        PASS_BUTTON: 'pass_button',
        ACTION_BUTTON_BG: 'action_button_bg',
        GIVE_DON_BUTTON_TEXT: 'give_don_button_text',
        DECLARE_ATTACK_BUTTON_TEXT: 'declare_attack_button_text',
        ACTIVATE_EFFECT_BUTTON_TEXT: 'activate_effect_button_text',
        WATER_BG: 'water_bg',
        WATER_WAVE: 'water_wave',
        ATTACK_ARROW: 'attack_arrow',
        PARTICLE_CIRCLE: 'particle_circle',
        CARD_DON_COUNT: 'card_don_count',
        RESTED_OVERLAY: 'rested_overlay',
        RESTED_ZZZ: 'rested_zzz',
        PROMPT_BG: 'prompt_bg',
        SUMMONING_SICKNESS: 'summoning_sickness',
        ACTION_BUTTONS_BG: 'action_buttons_bg',
        CIRCLE_01: 'circle_01',
        CIRCLE_02: 'circle_02',
        CIRCLE_06: 'circle_06',
        WHITE_CARD: 'white_card',
        SKULL: 'skull',
        PLAY_ACTION_BUTTON: 'play_action_button',
        BLOCK_ACTION_BUTTON: 'block_action_button',
        COUNTER_ACTION_BUTTON: 'counter_action_button',
        RESET_ACTION_BUTTON: 'reset_action_button',
        CONFIRM_ACTION_BUTTON: 'confirm_action_button',
        KEEP_ACTION_BUTTON: 'keep_action_button',
        ACTIVATE_TRIGGER_ACTION_BUTTON: 'activate_trigger_action_button',
        NO_SELECTION_ACTION_BUTTON: 'no_selection_action_button',
        PLACE_ON_TOP_ACTION_BUTTON: 'place_on_top_action_button',
        PLACE_ON_BOTTOM_ACTION_BUTTON: 'place_on_bottom_action_button',
        VICTORY: 'victory',
        DEFEAT: 'defeat',
        HIT_SOUND: 'hit_sound',
        YOUR_TURN: 'your_turn',
        OPPONENTS_TURN: 'opponents_turn',
        SHIP_BELL_SOUND: 'ship_bell_sound',
        SEAGULL_SOUND: 'seagull_sound',
        SHIP_BELL_WITH_SEAGULLS_SOUND: 'ship_bell_with_seagulls_sound',
        BG_MUSIC: 'bg_music',
        BLOCKER_ICON: 'blocker_icon',
        BLOCKER_FRAME: 'blocker_frame',
        BLOCKER_FRAME_HIGHLIGHT_MASK: 'blocker_frame_highlight_mask',
        BLOCKER_FRAME_CLOSE_SOUND: 'blocker_frame_close_sound',
        CHARACTER_PLAYED_SOUND: 'character_played_sound',
        CARD_DRAW_SOUND: 'card_draw_sound',
        HEART_DAMAGE_SPRITESHEET: 'heart_damage_spritesheet',
        CROWS_SOUND: 'crows_sound',
        CLOSE_CARD_INFO_X: 'close_card_info_x',
        CARD_INFO_LEADER_ICON: 'card_info_leader_icon',
        CARD_INFO_CHARACTER_ICON: 'card_info_character_icon',
        CARD_INFO_STAGE_ICON: 'card_info_stage_icon',
        CARD_INFO_EVENT_ICON: 'card_info_event_icon',
        WAVES_FOOTER: 'waves_footer',
        SETTINGS_BUTTON: 'settings_button',
        SETTINGS_SURRENDER_BUTTON: 'settings_surrender_button',
        SETTINGS_CHOPPER_HIDING: 'settings_chopper_hiding',
        EFFECT_ACTIVATION_SOUND: 'effect_activation_sound',
        FIRE_RING: 'fire_ring',
        CARD_GLOW: 'card_glow',
        FTUE_POPUP_INFO: 'ftue_popup_info',
        SIDE_BUTTON_HIGHLIGHT_MASK: 'side_button_highlight_mask',
        REMATCH_BUTTON: 'rematch_button',
        QUIT_MATCH_BUTTON: 'quit_match_button',
        GOING_FIRST: 'going_first',
        GOING_SECOND: 'going_second',
        COUNTER_SOUND: 'counter_sound',
        COUNTER_BUBBLE: 'counter_bubble',
        COUNTER_BUBBLE_SOUND: 'counter_bubble_sound',
        REORDER_SLOT: 'reorder_slot',
        RIGHT_ARROW: 'right_arrow',
        NO_SELECTION_SLOT: 'no_selection_slot',
        COIN: 'coin',

        ST03001_ANIMATION_1: 'st03001_animation_1',
        ST03001_ANIMATION_2: 'st03001_animation_2',
        ST03001_ANIMATION_3: 'st03001_animation_3',
    }

    static async init() {
        await PIXI.Assets.load([
            { src: '/assets/main_deck_card_back.png', alias: this.eDefaultAssetsAliases.MAIN_DECK_CARD_BACK },
            { src: '/assets/don_card_back.png', alias: this.eDefaultAssetsAliases.DON_DECK_CARD_BACK },
            { src: '/assets/don_card_face.png', alias: this.eDefaultAssetsAliases.DON_CARD_FACE },
            { src: '/assets/player_ship.png', alias: this.eDefaultAssetsAliases.PLAYER_BACKGROUND },
            { src: '/assets/enemy_ship.png', alias: this.eDefaultAssetsAliases.OPPONENT_BACKGROUND },
            { src: '/assets/Game_Asset_0006_Card-Deck.png', alias: this.eDefaultAssetsAliases.MAIN_DECK },
            { src: '/assets/leader_frame.png', alias: this.eDefaultAssetsAliases.LEADER_FRAME },
            { src: '/assets/life_icon.png', alias: this.eDefaultAssetsAliases.LIFE_ICON },
            { src: '/assets/don_deck.png', alias: this.eDefaultAssetsAliases.DON_DECK },
            { src: '/assets/active_dons.png', alias: this.eDefaultAssetsAliases.ACTIVE_DONS },
            { src: '/assets/active_dons_frame.png', alias: this.eDefaultAssetsAliases.ACTIVE_DONS_FRAME },
            { src: '/assets/rested_dons.png', alias: this.eDefaultAssetsAliases.RESTED_DONS },
            { src: '/assets/stage_frame.png', alias: this.eDefaultAssetsAliases.STAGE_FRAME },
            { src: '/assets/trash_frame.png', alias: this.eDefaultAssetsAliases.TRASH_FRAME },
            { src: '/assets/card_fade.png', alias: this.eDefaultAssetsAliases.CARD_FADE },
            { src: '/assets/smoke_effect_noise.png', alias: this.eDefaultAssetsAliases.HIGHLIGHT_EFFECT_NOISE },
            { src: '/assets/card_inner_frame_large.png', alias: this.eDefaultAssetsAliases.CARD_INNER_FRAME },
            { src: '/assets/phase_button.png', alias: this.eDefaultAssetsAliases.PHASE_BUTTON },
            { src: '/assets/gameplay/side_buttons/cancel_button.png', alias: this.eDefaultAssetsAliases.CANCEL_BUTTON },
            { src: '/assets/gameplay/side_buttons/end_turn_button.png', alias: this.eDefaultAssetsAliases.END_TURN_BUTTON },
            { src: '/assets/gameplay/side_buttons/enemy_blocker_button.png', alias: this.eDefaultAssetsAliases.ENEMY_BLOCKER_BUTTON },
            { src: '/assets/gameplay/side_buttons/enemy_counter_button.png', alias: this.eDefaultAssetsAliases.ENEMY_COUNTER_BUTTON },
            { src: '/assets/gameplay/side_buttons/enemy_turn_button.png', alias: this.eDefaultAssetsAliases.ENEMY_TURN_BUTTON },
            { src: '/assets/gameplay/side_buttons/pass_button.png', alias: this.eDefaultAssetsAliases.PASS_BUTTON },
            { src: '/assets/action_button_bg.png', alias: this.eDefaultAssetsAliases.ACTION_BUTTON_BG },
            { src: '/assets/gameplay/action_buttons/give_don.png', alias: this.eDefaultAssetsAliases.GIVE_DON_BUTTON_TEXT },
            { src: '/assets/gameplay/action_buttons/attack.png', alias: this.eDefaultAssetsAliases.DECLARE_ATTACK_BUTTON_TEXT },
            { src: '/assets/gameplay/action_buttons/activate_effect.png', alias: this.eDefaultAssetsAliases.ACTIVATE_EFFECT_BUTTON_TEXT },
            { src: '/assets/water_bg.png', alias: this.eDefaultAssetsAliases.WATER_BG },
            { src: '/assets/ripple.png', alias: this.eDefaultAssetsAliases.WATER_WAVE },
            { src: '/assets/attack_arrow.png', alias: this.eDefaultAssetsAliases.ATTACK_ARROW },
            { src: '/assets/circle_05.png', alias: this.eDefaultAssetsAliases.PARTICLE_CIRCLE },
            { src: '/assets/card_don_count.png', alias: this.eDefaultAssetsAliases.CARD_DON_COUNT },
            { src: '/assets/rested_overlay.png', alias: this.eDefaultAssetsAliases.RESTED_OVERLAY },
            { src: '/assets/rested_zzz.png', alias: this.eDefaultAssetsAliases.RESTED_ZZZ },
            { src: '/assets/prompt_bg.png', alias: this.eDefaultAssetsAliases.PROMPT_BG },
            { src: '/assets/summoning_sickness.png', alias: this.eDefaultAssetsAliases.SUMMONING_SICKNESS },
            { src: '/assets/action_buttons_bg.png', alias: this.eDefaultAssetsAliases.ACTION_BUTTONS_BG },
            { src: '/assets/circle_01.png', alias: this.eDefaultAssetsAliases.CIRCLE_01 },
            { src: '/assets/circle_02.png', alias: this.eDefaultAssetsAliases.CIRCLE_02 },
            { src: '/assets/white_card.png', alias: this.eDefaultAssetsAliases.WHITE_CARD },
            { src: '/assets/circle_06.png', alias: this.eDefaultAssetsAliases.CIRCLE_06 },
            { src: '/assets/skull.png', alias: this.eDefaultAssetsAliases.SKULL },
            { src: '/assets/gameplay/action_buttons/play_card.png', alias: this.eDefaultAssetsAliases.PLAY_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/block.png', alias: this.eDefaultAssetsAliases.BLOCK_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/counter.png', alias: this.eDefaultAssetsAliases.COUNTER_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/reset.png', alias: this.eDefaultAssetsAliases.RESET_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/confirm.png', alias: this.eDefaultAssetsAliases.CONFIRM_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/keep.png', alias: this.eDefaultAssetsAliases.KEEP_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/activate_trigger_effect.png', alias: this.eDefaultAssetsAliases.ACTIVATE_TRIGGER_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/no_selection.png', alias: this.eDefaultAssetsAliases.NO_SELECTION_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/place_on_top.png', alias: this.eDefaultAssetsAliases.PLACE_ON_TOP_ACTION_BUTTON },
            { src: '/assets/gameplay/action_buttons/place_on_bottom.png', alias: this.eDefaultAssetsAliases.PLACE_ON_BOTTOM_ACTION_BUTTON },
            { src: '/assets/victory.png', alias: this.eDefaultAssetsAliases.VICTORY },
            { src: '/assets/defeat.png', alias: this.eDefaultAssetsAliases.DEFEAT },
            { src: '/assets/gameplay/your_turn2.png', alias: this.eDefaultAssetsAliases.YOUR_TURN },
            { src: '/assets/gameplay/opponents_turn.png', alias: this.eDefaultAssetsAliases.OPPONENTS_TURN },
            { src: '/assets/blocker_icon2.png', alias: this.eDefaultAssetsAliases.BLOCKER_ICON },
            { src: '/assets/blocker_frame.png', alias: this.eDefaultAssetsAliases.BLOCKER_FRAME },
            { src: '/assets/blocker_frame_highlight_mask.png', alias: this.eDefaultAssetsAliases.BLOCKER_FRAME_HIGHLIGHT_MASK },
            { src: '/assets/gameplay/spritesheets/heart_damage_spritesheet.json', alias: this.eDefaultAssetsAliases.HEART_DAMAGE_SPRITESHEET },
            { src: '/assets/card_info/close_card_info_x.png', alias: this.eDefaultAssetsAliases.CLOSE_CARD_INFO_X },
            { src: '/assets/card_info/leader_icon.png', alias: this.eDefaultAssetsAliases.CARD_INFO_LEADER_ICON },
            { src: '/assets/card_info/character_icon.png', alias: this.eDefaultAssetsAliases.CARD_INFO_CHARACTER_ICON },
            { src: '/assets/card_info/stage_icon.png', alias: this.eDefaultAssetsAliases.CARD_INFO_STAGE_ICON },
            { src: '/assets/card_info/event_icon.png', alias: this.eDefaultAssetsAliases.CARD_INFO_EVENT_ICON },
            { src: '/assets/card_info/waves_footer.png', alias: this.eDefaultAssetsAliases.WAVES_FOOTER },
            { src: '/assets/settings/settings_button.png', alias: this.eDefaultAssetsAliases.SETTINGS_BUTTON },
            { src: '/assets/settings/surrender_button.png', alias: this.eDefaultAssetsAliases.SETTINGS_SURRENDER_BUTTON },
            { src: '/assets/settings/chopper_hiding.png', alias: this.eDefaultAssetsAliases.SETTINGS_CHOPPER_HIDING },
            { src: '/assets/fire_ring.png', alias: this.eDefaultAssetsAliases.FIRE_RING },
            { src: '/assets/card_glow.png', alias: this.eDefaultAssetsAliases.CARD_GLOW },
            { src: '/assets/ftue_popup_info.png', alias: this.eDefaultAssetsAliases.FTUE_POPUP_INFO },
            { src: '/assets/side_button_highlight_mask.png', alias: this.eDefaultAssetsAliases.SIDE_BUTTON_HIGHLIGHT_MASK },
            { src: '/assets/rematch_button.png', alias: this.eDefaultAssetsAliases.REMATCH_BUTTON },
            { src: '/assets/quit_match_button.png', alias: this.eDefaultAssetsAliases.QUIT_MATCH_BUTTON },
            { src: '/assets/going_first.png', alias: this.eDefaultAssetsAliases.GOING_FIRST },
            { src: '/assets/going_second.png', alias: this.eDefaultAssetsAliases.GOING_SECOND },
            { src: '/assets/counter_bubble.png', alias: this.eDefaultAssetsAliases.COUNTER_BUBBLE },
            { src: '/assets/reorder_slot.png', alias: this.eDefaultAssetsAliases.REORDER_SLOT },
            { src: '/assets/right_arrow.png', alias: this.eDefaultAssetsAliases.RIGHT_ARROW },
            { src: '/assets/no_selection_slot.png', alias: this.eDefaultAssetsAliases.NO_SELECTION_SLOT },
            { src: '/assets/shop/coin.png', alias: this.eDefaultAssetsAliases.COIN },

            { src: '/assets/gameplay/animations/ST03-001/1.png', alias: this.eDefaultAssetsAliases.ST03001_ANIMATION_1 },
            { src: '/assets/gameplay/animations/ST03-001/2.png', alias: this.eDefaultAssetsAliases.ST03001_ANIMATION_2 },
            { src: '/assets/gameplay/animations/ST03-001/3.png', alias: this.eDefaultAssetsAliases.ST03001_ANIMATION_3 },
        ]);
        
        this.sounds = {
            [this.eDefaultAssetsAliases.HIT_SOUND]: await loadSound('/assets/hit_sound_v2.wav'),
            [this.eDefaultAssetsAliases.SHIP_BELL_WITH_SEAGULLS_SOUND]: await loadSound('/assets/gameplay/ship_bell_with_seagulls_sound.wav'),
            [this.eDefaultAssetsAliases.BG_MUSIC]: await loadSound('/assets/gameplay/to_the_grand_line_bg_music.mp3', { loop: true, volume: 0.35 }),
            [this.eDefaultAssetsAliases.BLOCKER_FRAME_CLOSE_SOUND]: await loadSound('/assets/blocker_frame_close_sound2.wav'),
            [this.eDefaultAssetsAliases.CHARACTER_PLAYED_SOUND]: await loadSound('/assets/character_played_sound.mp3'),
            [this.eDefaultAssetsAliases.CARD_DRAW_SOUND]: await loadSound('/assets/card_draw_sound.wav'),
            [this.eDefaultAssetsAliases.CROWS_SOUND]: await loadSound('/assets/gameplay/crows_sound.wav'),
            [this.eDefaultAssetsAliases.EFFECT_ACTIVATION_SOUND]: await loadSound('/assets/gameplay/effect_activation_sound3.mp3', { volume: 0.5 }),
            [this.eDefaultAssetsAliases.COUNTER_SOUND]: await loadSound('/assets/gameplay/counter_sound.wav'),
            [this.eDefaultAssetsAliases.COUNTER_BUBBLE_SOUND]: await loadSound('/assets/counter_bubble_sound.mp3'),
        };

        await PIXI.Assets.load({
            src: 'https://fonts.gstatic.com/s/opensans/v44/memvYaGs126MiZpBA-UvWbX2vVnXBbObj2OVTS-muw.woff2',
            data: {
                family: 'OpenSans',
                weights: ['600'],
                unicodeRange: 'U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD',
            }
        });
    }

    static async loadDecks(decks) {
        const assets = [];
        for (const deck of decks) {
            deck.forEach(card => {
                assets.push(card.imageUrl);
            });
        }

        await PIXI.Assets.load(assets);
    }

    static getSound(alias) {
        return this.sounds?.[alias];
    }
}
